/**
 * W13 DM2 — the «Why this copy» rows of `DataMappingViewerV2`.
 *
 * AC-W13-09: one row per cited thesis (canonical text, category, the entry's
 * justification); the quote citations of that thesis INDENTED beneath it, each
 * showing the quoted words, «— <actor>» and its own justification; a quote whose
 * thesis this screen does not cite under the «Quote without its thesis» heading
 * with the DM1 warning — never dropped, never blank.
 *
 * No new layout language: the same `action.hover` card, the same caption /
 * monospace-path typography and the same `divider` left rule the viewer's
 * synthesis group already uses. Theme tokens only.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { EDITOR } from '@test-kit/selectors/editor';

import {
  EVIDENCE_SECTION_TITLE,
  LONE_QUOTE_HEADING,
  LONE_QUOTE_NOTE,
  extraLines,
  resolveQuote,
  resolveThesis,
  type EvidenceCitationGroup,
  type GroupedEvidence,
  type InsightEvidenceState,
} from './insightEvidence';

/**
 * The inline «why this row cannot say more» line — the row is never blank.
 *
 * `text.secondary`, not `warning.main`: DESIGN.md §Chips records that
 * `warning.main` on light paper is 3.11:1 and under AA for 13px label text, and
 * this is a 12px caption. The WORDS carry the warning, colour never does.
 *
 * REVIEW tier 2 MF-1: when the citation could NOT be resolved this note is what
 * the row leads with — `body2` in `text.primary`, because it is then the row's
 * primary content and the raw path must never carry that job alone.
 */
const Note: React.FC<{ children: string; lead?: boolean }> = ({ children, lead }) => (
  <Typography
    variant={lead ? 'body2' : 'caption'}
    sx={{ display: 'block', color: lead ? 'text.primary' : 'text.secondary', fontStyle: 'italic' }}
  >
    {children}
  </Typography>
);

/**
 * The raw citation path — the traceable id under every row.
 *
 * REVIEW tier 2 MF-1: `text.secondary` (7.14:1 light / 7.98:1 dark on this
 * surface), NOT the `text.disabled` the item cards use (2.65:1 / 4.28:1, under
 * AA). It is an operator's copy-paste handle and, in the unresolved state, the
 * only unambiguous identity the row has — it has to be readable.
 */
const SourcePath: React.FC<{ path: string }> = ({ path }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      fontFamily: 'monospace',
      color: 'text.secondary',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}
  >
    {path}
  </Typography>
);

/** The «also: …» line of every FURTHER entry citing the same path (I1 / M1). */
const AlsoLines: React.FC<{ group: EvidenceCitationGroup }> = ({ group }) => (
  <>
    {extraLines(group).map((line, index) => (
      <Typography
        // The lines are positional by nature — an entry has no id of its own.
        key={`${group.citation.sourcePath}-also-${index}`}
        variant="caption"
        sx={{ display: 'block', color: 'text.secondary' }}
      >
        {line}
      </Typography>
    ))}
  </>
);

const QuoteRow: React.FC<{
  group: EvidenceCitationGroup;
  evidence: InsightEvidenceState;
  testId: string;
}> = ({ group, evidence, testId }) => {
  const { citation } = group;
  const { text, actorName, note } = resolveQuote(evidence, citation);
  const justification = citation.item.justification;

  return (
    <Box
      data-testid={testId}
      sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}
    >
      {/* The words are the evidence; the actor is attribution — secondary. */}
      {text ? (
        <Typography variant="body2">
          {`“${text}”`}
          <Box component="span" sx={{ color: 'text.secondary' }}>{` — ${actorName}`}</Box>
        </Typography>
      ) : (
        // MF-1: nothing to quote ⇒ the note leads, the ids follow underneath.
        <>
          {note && <Note lead>{note}</Note>}
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            {`${citation.parsed.sourceItemId} — ${actorName}`}
          </Typography>
        </>
      )}
      {typeof justification === 'string' && justification.trim() && (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
          {justification}
        </Typography>
      )}
      <AlsoLines group={group} />
      {/* Resolved text but an unknown item id: the note is detail, not the lead. */}
      {note && text && <Note>{note}</Note>}
      <SourcePath path={citation.sourcePath} />
    </Box>
  );
};

export interface InsightEvidenceSectionProps {
  grouped: GroupedEvidence;
  evidence: InsightEvidenceState;
  /**
   * REVIEW tier 2 (should-fix): the panel name is printed ONCE. The accordion
   * that already carries «Why this copy» as its title suppresses this heading;
   * a viewer titled anything else keeps it.
   */
  showTitle?: boolean;
}

export const InsightEvidenceSection: React.FC<InsightEvidenceSectionProps> = ({
  grouped,
  evidence,
  showTitle = true,
}) => {
  const { groups, loneQuotes } = grouped;
  if (groups.length === 0 && loneQuotes.length === 0) return null;

  return (
    <Box data-testid={EDITOR.aiMetaEvidence.section}>
      {showTitle && (
        <Typography variant="subtitle2" gutterBottom>{EVIDENCE_SECTION_TITLE}</Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {groups.map((group) => {
          const { parsed, sourcePath, item } = group.citation;
          const { text, category, note } = resolveThesis(evidence, parsed);
          const justification = item.justification;

          return (
            <Box
              key={sourcePath}
              data-testid={EDITOR.aiMetaEvidence.thesisRow(parsed.insightId)}
              // REVIEW tier 2: the same left-rule the viewer's synthesis card
              // uses, so a thesis row is not mistaken for a plain item card.
              sx={{
                p: 1,
                borderRadius: 1,
                bgcolor: 'action.hover',
                borderLeft: '3px solid',
                borderColor: 'divider',
              }}
            >
              {/* MF-1: unresolved ⇒ the note is the row's opening line, never the path. */}
              {text ? (
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{text}</Typography>
              ) : (
                note && <Note lead>{note}</Note>
              )}
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                {category}
              </Typography>
              {typeof justification === 'string' && justification.trim() && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {justification}
                </Typography>
              )}
              <AlsoLines group={group} />
              <SourcePath path={sourcePath} />
              {group.quotes.map((quote) => (
                <QuoteRow
                  key={quote.citation.parsed.sourceItemId}
                  group={quote}
                  evidence={evidence}
                  testId={EDITOR.aiMetaEvidence.quoteRow(parsed.insightId, quote.citation.parsed.sourceItemId ?? '')}
                />
              ))}
            </Box>
          );
        })}
      </Box>

      {loneQuotes.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography
            variant="subtitle2"
            data-testid={EDITOR.aiMetaEvidence.loneQuoteHeading}
          >
            {LONE_QUOTE_HEADING}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            {LONE_QUOTE_NOTE}
          </Typography>
          {loneQuotes.map((quote) => (
            <QuoteRow
              key={`${quote.citation.parsed.thesisPath}|${quote.citation.parsed.sourceItemId}`}
              group={quote}
              evidence={evidence}
              testId={EDITOR.aiMetaEvidence.loneQuoteRow(quote.citation.parsed.sourceItemId ?? '')}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
